/**
 * AI Buck Help Content Library
 * 
 * Centralized help content following AI Buck Tone and Language Guidelines.
 * Reference: /app/memory/AI_BUCK_GUIDELINES.md
 * 
 * Usage: Import the help content for the specific page and pass to HelpPanel component.
 */

// ============================================
// BANKING PAGE HELP CONTENT
// ============================================
export const bankingHelp = {
  tourId: "banking",
  title: "Banking Help",
  description: "Learn how to manage and categorize bank transactions",
  sections: [
    {
      heading: "Transaction Statuses",
      content: (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs rounded bg-yellow-100 text-yellow-800">Pending</span>
            <span>New transaction awaiting review</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800">For Review</span>
            <span>Needs attention before posting</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-800">Categorized</span>
            <span>Account assigned, ready to post</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs rounded bg-indigo-100 text-indigo-800">Matched</span>
            <span>Linked to an existing invoice or bill</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs rounded bg-emerald-100 text-emerald-800">Posted</span>
            <span>Recorded in your general ledger</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-800">Excluded</span>
            <span>Ignored and will not be posted</span>
          </div>
        </div>
      )
    },
    {
      heading: "AI Buck vs Manual Actions",
      type: "info",
      content: "AI Buck analyzes transaction patterns and suggests categories based on vendor history, amount, and description. You can accept AI Buck suggestions with one click, or manually select any account. AI Buck learns from your corrections to improve future suggestions."
    },
    {
      heading: "Posting Transactions",
      content: "When you post a transaction, it creates a journal entry in your general ledger. Posted transactions affect your financial statements. Only post transactions you've verified are correctly categorized."
    },
    {
      heading: "Excluding Transactions",
      type: "warning",
      content: "Excluding a transaction removes it from your workflow without posting. Use this for transfers between your own accounts, personal expenses on a business card, or duplicate entries. Excluded transactions can be restored if needed."
    }
  ],
  tips: [
    "Use AI Buck to process multiple transactions at once",
    "Set up rules for recurring vendors to auto-categorize future transactions",
    "Review the 'For Review' tab regularly to catch uncategorized items",
    "Match payments to invoices to keep AR/AP accurate"
  ],
  warnings: [
    "Posted transactions create permanent ledger entries",
    "Excluded transactions won't appear in your financial reports"
  ]
};

// ============================================
// AI BUCK PAGE HELP CONTENT
// ============================================
export const aiBuckHelp = {
  title: "AI Buck Help",
  description: "Understanding AI-powered transaction categorization",
  sections: [
    {
      heading: "What AI Buck Does",
      content: "AI Buck analyzes your bank transactions and suggests the most likely expense or income category. It learns from your transaction history, vendor patterns, and manual corrections to improve accuracy over time."
    },
    {
      heading: "Confidence Levels",
      type: "info",
      content: (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs rounded bg-emerald-100 text-emerald-800">High (≥85%)</span>
            <span>AI Buck is confident—quick review sufficient</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-800">Medium (70-84%)</span>
            <span>AI Buck suggests reviewing before accepting</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs rounded bg-rose-100 text-rose-800">Low (&lt;70%)</span>
            <span>AI Buck needs your input—manual selection recommended</span>
          </div>
        </div>
      )
    },
    {
      heading: "Operating Modes",
      content: (
        <div className="space-y-2">
          <p><strong>Suggest Only:</strong> AI Buck queues suggestions for your review. Nothing posts without your approval.</p>
          <p><strong>Auto Apply:</strong> High-confidence suggestions are automatically applied. You set the confidence threshold.</p>
          <p><strong>Off:</strong> AI Buck is disabled. All categorization is manual.</p>
        </div>
      )
    },
    {
      heading: "When AI Buck Acts vs Recommends",
      type: "warning",
      content: "In 'Suggest Only' mode, AI Buck never posts transactions automatically—it only adds suggestions to your queue. In 'Auto Apply' mode, transactions meeting your confidence threshold are posted automatically. AI Buck always shows what it did in the activity log."
    },
    {
      heading: "Rules",
      content: "Rules are deterministic overrides. When a transaction matches a rule (by vendor, amount, or description), AI Buck applies the rule's category regardless of its own suggestion. Rules always take priority over AI suggestions."
    }
  ],
  tips: [
    "Start with 'Suggest Only' mode until you trust AI Buck's accuracy",
    "Create rules for recurring vendors like utilities or subscriptions",
    "Use 'Select High Confidence' to batch-approve reliable suggestions",
    "Review low-confidence suggestions—your corrections help AI Buck learn"
  ],
  warnings: [
    "Auto Apply mode posts transactions without manual review",
    "Rules override AI suggestions—make sure rules are correct"
  ]
};

// ============================================
// BILLS & INVOICES PAGE HELP CONTENT
// ============================================
export const billsHelp = {
  tourId: "bills",
  title: "Bills Help",
  description: "Managing accounts payable",
  sections: [
    {
      heading: "Bill Workflow",
      content: (
        <div className="space-y-2">
          <p><strong>Draft:</strong> Bill is entered but not yet approved. Can be edited freely.</p>
          <p><strong>Approved:</strong> Bill is verified and ready to post. Edits are restricted.</p>
          <p><strong>Posted:</strong> Bill is recorded in your ledger and affects your AP balance.</p>
          <p><strong>Paid:</strong> Payment has been recorded against this bill.</p>
        </div>
      )
    },
    {
      heading: "OCR Upload",
      type: "info",
      content: "Upload a PDF or image of a vendor bill, and AI Buck extracts the vendor, date, amounts, and line items. Review the extraction, make corrections if needed, and save as a draft. OCR uploads always create drafts—nothing posts automatically."
    },
    {
      heading: "Confidence in OCR",
      content: (
        <div className="space-y-2">
          <p>AI Buck shows confidence scores for each extracted field:</p>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs rounded bg-emerald-100 text-emerald-800">High</span>
            <span>Extraction is likely correct</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-800">Medium</span>
            <span>Please verify this field</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs rounded bg-rose-100 text-rose-800">Low</span>
            <span>Manual entry recommended</span>
          </div>
        </div>
      )
    },
    {
      heading: "Matching to Bank Transactions",
      content: "When you pay a bill, match the bank transaction to the bill to clear it from your AP. This keeps your accounts payable balance accurate and helps with reconciliation."
    }
  ],
  tips: [
    "Use OCR upload to speed up bill entry from paper or PDF invoices",
    "Review all fields before saving—AI Buck highlights uncertain extractions",
    "Match bills to bank transactions to track what's paid",
    "Use the 'Recent OCR Uploads' section to resume incomplete scans"
  ],
  warnings: [
    "Posted bills affect your financial statements",
    "OCR creates drafts only—review before approving"
  ]
};

export const invoicesHelp = {
  tourId: "invoices",
  title: "Invoices Help",
  description: "Managing accounts receivable",
  sections: [
    {
      heading: "Invoice Workflow",
      content: (
        <div className="space-y-2">
          <p><strong>Draft:</strong> Invoice is created but not sent. Can be edited freely.</p>
          <p><strong>Sent:</strong> Invoice has been sent to the customer.</p>
          <p><strong>Posted:</strong> Invoice is recorded in your ledger and affects your AR balance.</p>
          <p><strong>Paid:</strong> Payment has been received and recorded.</p>
          <p><strong>Void:</strong> Invoice has been cancelled with a reversing entry.</p>
        </div>
      )
    },
    {
      heading: "Recurring Invoices",
      type: "info",
      content: (
        <div className="space-y-2">
          <p>Set up invoices that automatically generate on a schedule. Perfect for retainers, subscriptions, and regular billing.</p>
          <p><strong>How to set up:</strong></p>
          <ol className="list-decimal list-inside space-y-1 text-sm">
            <li>Click the <strong>"Recurring"</strong> tab at the top of the Invoices page</li>
            <li>Click <strong>"New Recurring Invoice"</strong></li>
            <li>Choose a customer, frequency (weekly, monthly, quarterly, yearly), and start date</li>
            <li>Add your line items (description, quantity, price, account)</li>
            <li>Choose whether to <strong>auto-post</strong> (goes live automatically) or <strong>create as draft</strong> (you review first)</li>
            <li>When an invoice is due, click <strong>"Generate"</strong> to create it, or it will show a due alert</li>
          </ol>
          <p><strong>Tip:</strong> You can pause and resume recurring invoices at any time, and set an optional end date to stop them automatically.</p>
        </div>
      )
    },
    {
      heading: "OCR Upload",
      type: "info",
      content: "Upload a PDF or image of a sales invoice, and AI Buck extracts the customer, date, amounts, and line items. This is useful for digitizing paper records or importing invoices from other systems. OCR uploads always create drafts."
    },
    {
      heading: "Duplicate Detection",
      type: "warning",
      content: "AI Buck checks for potential duplicates based on customer, invoice number, amount, and date. If a match is found, you'll see a warning. You can still create the invoice if you're sure it's not a duplicate."
    }
  ],
  tips: [
    "Post invoices promptly to keep AR reports accurate",
    "Match customer payments to invoices to clear them from AR",
    "Use the aging report to follow up on overdue invoices",
    "Set up recurring invoices for retainers and regular billing",
    "OCR upload helps digitize paper invoices quickly"
  ],
  warnings: [
    "Posted invoices affect your financial statements",
    "Voiding an invoice creates a reversing entry—it doesn't delete it",
    "Recurring invoices in auto-post mode will post immediately when generated"
  ]
};

// ============================================
// DASHBOARD PAGE HELP CONTENT
// ============================================
export const dashboardHelp = {
  tourId: "dashboard",
  title: "Dashboard Help",
  description: "Understanding your financial overview",
  sections: [
    {
      heading: "Financial Widgets",
      content: "Financial widgets show real numbers from your ledger: cash balances, income, expenses, receivables, and payables. These reflect posted transactions only—drafts and pending items are not included."
    },
    {
      heading: "Workflow Widgets",
      type: "info",
      content: "Workflow widgets show items requiring attention: uncategorized transactions, pending approvals, overdue invoices, and AI Buck suggestions. These help you stay on top of your bookkeeping tasks."
    },
    {
      heading: "Understanding Widget Visuals",
      content: (
        <div className="space-y-3">
          <div>
            <p className="font-medium text-sm mb-1">Sparklines (wavy line)</p>
            <p className="text-xs text-muted-foreground">Show the trend direction over the selected time period. A rising line means the value is increasing; a falling line means it is decreasing. Hover for specific values.</p>
          </div>
          <div>
            <p className="font-medium text-sm mb-1">Mini Bar Charts</p>
            <p className="text-xs text-muted-foreground">Show activity volume by period (usually weekly). Taller bars = more activity that week. Helps you spot patterns in transaction volume or task accumulation.</p>
          </div>
          <div>
            <p className="font-medium text-sm mb-1">Delta Indicators (↑ / ↓)</p>
            <p className="text-xs text-muted-foreground">Show change from previous period. Green = improvement, Red = concern. For expenses and burn rate, lower is better (colors invert).</p>
          </div>
          <div>
            <p className="font-medium text-sm mb-1">Age Badges</p>
            <p className="text-xs text-muted-foreground">Appear on workflow widgets showing how old the oldest unresolved item is. Older items may need more urgent attention.</p>
          </div>
        </div>
      )
    },
    {
      heading: "AI Buck Insights",
      content: "When you see 'AI Buck' on the dashboard, it means the information includes analysis or recommendations—not just raw data. AI Buck insights highlight trends, anomalies, or suggested actions based on your financial patterns."
    },
    {
      heading: "Company Views",
      content: (
        <div className="space-y-2">
          <p><strong>Single Company:</strong> Shows data for the currently selected company only.</p>
          <p><strong>Consolidated (Combined):</strong> Aggregates data across all companies you have access to. Useful for multi-entity businesses. Look for the Combined badge in widget headers.</p>
        </div>
      )
    },
    {
      heading: "Customizing Your Dashboard",
      type: "info",
      content: "Click 'Customize Dashboard' to add, remove, or rearrange widgets. Drag widgets to reposition them. The Widget Library shows all available widgets organized by category. Your layout is saved automatically."
    },
    {
      heading: "Bank Connections",
      type: "warning",
      content: "Bank balances update when your connected accounts sync. If a balance looks stale, check the 'Last synced' timestamp or refresh the connection. Manual accounts require manual balance updates."
    }
  ],
  tips: [
    "Check the dashboard daily to catch items needing attention",
    "Use workflow widgets to prioritize your bookkeeping tasks",
    "Click any widget to dive into the detailed view",
    "Sparklines show direction, not just current values—watch for trends",
    "High age badges on workflow widgets indicate urgent backlogs"
  ],
  warnings: [
    "Dashboard shows posted transactions only—pending items are not reflected",
    "Bank balances may be delayed depending on your connection"
  ]
};

// ============================================
// BANK RECONCILIATION HELP CONTENT
// (Already exists, including here for completeness)
// ============================================
export const reconciliationHelp = {
  title: "Bank Reconciliation Help",
  description: "Understanding and completing your bank reconciliation",
  sections: [
    {
      heading: "What is Cleared?",
      content: "A 'Cleared' transaction has been matched to a bank statement line. It means the transaction appears on your bank statement and has been verified. Cleared transactions count toward your reconciled balance."
    },
    {
      heading: "What is Outstanding?",
      type: "info",
      content: "An 'Outstanding' transaction is in your books but hasn't appeared on the bank statement yet. This is normal for recent transactions, checks that haven't been cashed, or pending transfers."
    },
    {
      heading: "The Difference Amount",
      type: "warning",
      content: "The difference shows how far off your book balance is from your bank statement. A $0.00 difference means you're reconciled. A non-zero difference means there are unmatched transactions or errors to investigate."
    },
    {
      heading: "AI Buck Matching",
      content: "AI Buck analyzes transactions and suggests matches between your books and bank statement. High-confidence matches can be approved quickly. Low-confidence matches should be reviewed carefully."
    }
  ],
  tips: [
    "Reconcile regularly—monthly at minimum, weekly for high-volume accounts",
    "Start by reviewing AI Buck's suggested matches",
    "Check outstanding items for anything older than expected",
    "A zero difference doesn't guarantee accuracy—spot-check a few matches"
  ],
  warnings: [
    "Don't force a reconciliation to balance—investigate differences",
    "Outstanding checks older than 90 days may need to be voided"
  ]
};

// ============================================
// RECONCILE BUCK HELP CONTENT
// ============================================
export const reconcileBuckHelp = {
  title: "Smart Reconcile Help",
  description: "AI-assisted bank statement reconciliation",
  sections: [
    {
      heading: "How Smart Reconcile Works",
      content: "Smart Reconcile analyzes your uploaded bank statement against your book transactions. It suggests matches based on amounts, dates, and descriptions. You review the suggestions and approve or reject each one."
    },
    {
      heading: "Match Confidence",
      type: "info",
      content: (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs rounded bg-emerald-100 text-emerald-800">High (≥85%)</span>
            <span>AI Buck found a strong match—likely correct</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-800">Medium (70-84%)</span>
            <span>AI Buck sees a possible match—please verify</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs rounded bg-rose-100 text-rose-800">Low (&lt;70%)</span>
            <span>AI Buck is uncertain—manual review needed</span>
          </div>
        </div>
      )
    },
    {
      heading: "Unmatched Items",
      content: "Unmatched statement lines may indicate transactions not yet in your books (create them), or timing differences (they'll match next period). Unmatched book transactions are outstanding items."
    },
    {
      heading: "Completing Reconciliation",
      type: "warning",
      content: "When all matches are approved and the difference is zero, you can complete the reconciliation. This locks the period and moves matched transactions to 'Cleared' status."
    }
  ],
  tips: [
    "Process high-confidence matches first to clear the easy items",
    "Use 'Approve All High Confidence' to batch-approve reliable matches",
    "Investigate unmatched items before forcing a balance",
    "Run Smart Reconcile after uploading a new statement"
  ],
  warnings: [
    "Completing reconciliation locks the period—verify everything first",
    "Rejected matches return items to the unmatched queue"
  ]
};

// ============================================
// PDF STATEMENT UPLOAD HELP CONTENT
// ============================================
export const pdfStatementHelp = {
  title: "PDF Bank Statement Help",
  description: "Uploading and extracting data from PDF bank statements",
  sections: [
    {
      heading: "What Works",
      type: "info",
      content: (
        <div className="space-y-2">
          <p><strong>Digital PDFs:</strong> Statements downloaded directly from your bank or exported from banking software work best.</p>
          <p>These PDFs contain embedded text that can be extracted reliably.</p>
        </div>
      )
    },
    {
      heading: "What Does Not Work (Yet)",
      type: "warning",
      content: (
        <div className="space-y-2">
          <p><strong>Scanned PDFs:</strong> Paper statements that were scanned to PDF are not supported in this phase.</p>
          <p>For scanned statements, please convert to CSV using your bank online tools or upload as OFX/QFX.</p>
        </div>
      )
    },
    {
      heading: "Common Issues Checklist",
      content: (
        <div className="space-y-2">
          <p><strong>Missing dates:</strong> Some PDFs show dates only on the first row of each day. You may need to fill in dates manually.</p>
          <p><strong>Merged rows:</strong> Multi-line descriptions sometimes get merged. Split them if needed.</p>
          <p><strong>Negative sign issues:</strong> Debits might appear as positive numbers. Verify the sign of each amount.</p>
          <p><strong>Totals mismatch:</strong> Summary rows may be included as transactions. Delete total/summary rows.</p>
        </div>
      )
    },
    {
      heading: "Manual Editing",
      content: "If extraction is incomplete, you can add, edit, or delete lines manually in the preview step. This ensures you can proceed even when automatic extraction fails."
    }
  ],
  tips: [
    "Download statements directly from your bank for best results",
    "Check that all dates are correctly parsed",
    "Verify positive amounts are deposits and negative are withdrawals",
    "Delete any summary or total rows that were incorrectly extracted",
    "Use OFX/QFX format if available—it is more reliable than PDF"
  ],
  warnings: [
    "Scanned PDFs require OCR and are not supported in this phase",
    "Always verify extracted data before running the matching engine"
  ]
};

// ============================================
// MONEY OUTLOOK (CASH FORECAST) HELP CONTENT
// ============================================
export const moneyOutlookHelp = {
  title: "Money Outlook Help",
  description: "Understanding AI Buck's cash forecast",
  sections: [
    {
      heading: "What is Money Outlook?",
      content: "Money Outlook is AI Buck's forward-looking cash analysis. It answers the question every business owner asks: 'How long will my cash last?' by projecting your cash position based on known obligations and expected income."
    },
    {
      heading: "Cash Runway",
      type: "info",
      content: (
        <div className="space-y-2">
          <p>Cash runway shows how many days until your projected cash balance reaches zero.</p>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs rounded bg-emerald-100 text-emerald-800">90+ days</span>
            <span>Healthy—comfortable cushion</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs rounded bg-emerald-100 text-emerald-800">30-89 days</span>
            <span>Stable—monitor regularly</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-800">14-29 days</span>
            <span>Warning—action may be needed</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-xs rounded bg-rose-100 text-rose-800">Under 14 days</span>
            <span>Critical—immediate attention required</span>
          </div>
        </div>
      )
    },
    {
      heading: "Forecast Modes",
      content: (
        <div className="space-y-2">
          <p><strong>Conservative:</strong> Assumes slower invoice collection (75%) and adds 15% buffer for unexpected expenses. Best for cautious planning.</p>
          <p><strong>Balanced:</strong> Uses your historical averages for collection and expenses. Middle-ground projection.</p>
          <p><strong>Optimistic:</strong> Assumes 95% on-time collection and 10% expense savings. Best-case scenario.</p>
        </div>
      )
    },
    {
      heading: "Where the Numbers Come From",
      type: "info",
      content: (
        <div className="space-y-2">
          <p><strong>Current Cash:</strong> Sum of all connected bank account balances (posted transactions only).</p>
          <p><strong>Due Out:</strong> Unpaid bills + AI Buck detected recurring expenses.</p>
          <p><strong>Expected In:</strong> Outstanding invoices + AI Buck detected recurring income.</p>
          <p><strong>Projected Balance:</strong> Current Cash + Expected In - Due Out.</p>
        </div>
      )
    },
    {
      heading: "AI Buck Pattern Detection",
      content: "AI Buck analyzes your last 90 days of transactions to detect recurring expenses and income. It looks for same vendor/customer transactions with similar amounts (within 20% variance) occurring multiple times. These patterns are labeled as AI Buck estimates."
    },
    {
      heading: "Improving Forecast Accuracy",
      type: "warning",
      content: "Forecasts improve with more data. AI Buck recommends at least 30 days of transaction history for basic forecasts and 90+ days for high-confidence projections. Enter known upcoming bills and invoices to improve short-term accuracy."
    }
  ],
  tips: [
    "Check Money Outlook weekly to stay ahead of cash constraints",
    "Use Conservative mode for important financial decisions",
    "Follow up on overdue invoices highlighted by AI Buck",
    "Enter known upcoming bills even before they arrive",
    "Compare different forecast periods to understand timing sensitivity"
  ],
  warnings: [
    "AI Buck estimates may vary from actual results",
    "Unexpected expenses not in your books will affect actual cash position",
    "Collection rates can change—invoices may pay slower than projected"
  ]
};

// ============================================
// CHART OF ACCOUNTS HELP CONTENT
// ============================================
export const chartOfAccountsHelp = {
  tourId: "chart_of_accounts",
  title: "Chart of Accounts Help",
  description: "Managing your account structure",
  sections: [
    {
      heading: "Account Types",
      content: (
        <div className="space-y-2">
          <p><strong>Assets:</strong> Things you own (cash, equipment, inventory, receivables).</p>
          <p><strong>Liabilities:</strong> What you owe (loans, payables, credit cards).</p>
          <p><strong>Equity:</strong> Owner investment and retained earnings.</p>
          <p><strong>Income:</strong> Revenue from sales and services.</p>
          <p><strong>Expenses:</strong> Costs of running your business.</p>
        </div>
      )
    },
    {
      heading: "Account Codes",
      type: "info",
      content: "Account codes help organize your chart. Common structure: 1xxx = Assets, 2xxx = Liabilities, 3xxx = Equity, 4xxx = Income, 5xxx+ = Expenses. You can customize this structure to fit your business."
    },
    {
      heading: "Sub-accounts",
      content: "Create sub-accounts to add detail without cluttering your top-level chart. Example: 'Office Supplies' as a sub-account of 'Operating Expenses'. Sub-account balances roll up to their parent."
    },
    {
      heading: "Deactivating Accounts",
      type: "warning",
      content: "When you deactivate an account, it's hidden from new transactions but remains in historical reports. You cannot delete accounts that have transaction history. Deactivate instead."
    }
  ],
  tips: [
    "Keep your chart simple—add accounts as needed, not in anticipation",
    "Use consistent naming conventions for easier searching",
    "Review inactive accounts periodically and reactivate if needed",
    "Sub-accounts help with detailed reporting without clutter"
  ],
  warnings: [
    "Changes to account types can affect financial statement presentation",
    "Accounts with history cannot be deleted, only deactivated"
  ]
};

// ============================================
// JOURNAL ENTRIES HELP CONTENT
// ============================================
export const journalEntriesHelp = {
  tourId: "journal_entries",
  title: "Journal Entries Help",
  description: "Recording transactions in your ledger",
  sections: [
    {
      heading: "Debit & Credit Quick Reference",
      content: (
        <div className="space-y-2">
          <p>Every transaction affects at least two accounts. Debits must always equal credits.</p>
          <div className="rounded-lg border overflow-hidden mt-2">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left p-2 font-semibold">Account Type</th>
                  <th className="text-center p-2 font-semibold">Debit</th>
                  <th className="text-center p-2 font-semibold">Credit</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t"><td className="p-2"><div className="font-medium">Assets</div><div className="text-muted-foreground text-[10px]">Cash, Equipment, A/R</div></td><td className="p-2 text-center font-semibold text-emerald-600 dark:text-emerald-400">+ Increase</td><td className="p-2 text-center font-semibold text-red-500 dark:text-red-400">- Decrease</td></tr>
                <tr className="border-t"><td className="p-2"><div className="font-medium">Liabilities</div><div className="text-muted-foreground text-[10px]">A/P, Loans, Credit Cards</div></td><td className="p-2 text-center font-semibold text-red-500 dark:text-red-400">- Decrease</td><td className="p-2 text-center font-semibold text-emerald-600 dark:text-emerald-400">+ Increase</td></tr>
                <tr className="border-t"><td className="p-2"><div className="font-medium">Equity</div><div className="text-muted-foreground text-[10px]">Owner's Equity, Retained Earnings</div></td><td className="p-2 text-center font-semibold text-red-500 dark:text-red-400">- Decrease</td><td className="p-2 text-center font-semibold text-emerald-600 dark:text-emerald-400">+ Increase</td></tr>
                <tr className="border-t"><td className="p-2"><div className="font-medium">Revenue</div><div className="text-muted-foreground text-[10px]">Sales, Service Income</div></td><td className="p-2 text-center font-semibold text-red-500 dark:text-red-400">- Decrease</td><td className="p-2 text-center font-semibold text-emerald-600 dark:text-emerald-400">+ Increase</td></tr>
                <tr className="border-t"><td className="p-2"><div className="font-medium">Expenses</div><div className="text-muted-foreground text-[10px]">Rent, Utilities, Payroll</div></td><td className="p-2 text-center font-semibold text-emerald-600 dark:text-emerald-400">+ Increase</td><td className="p-2 text-center font-semibold text-red-500 dark:text-red-400">- Decrease</td></tr>
              </tbody>
            </table>
          </div>
          <p className="text-[10px] mt-1 text-muted-foreground italic">Remember: Assets = Liabilities + Equity. Left side increases with debits, right side increases with credits.</p>
        </div>
      )
    },
    {
      heading: "Entry Statuses",
      type: "info",
      content: (
        <div className="space-y-2">
          <p><strong>Draft:</strong> Work in progress. Can be edited freely. Does not affect reports.</p>
          <p><strong>Posted:</strong> Finalized and recorded in the ledger. Affects all reports. Cannot be edited.</p>
          <p><strong>Void:</strong> Cancelled with a reversing entry. Original entry remains for audit trail.</p>
        </div>
      )
    },
    {
      heading: "Voiding vs Editing",
      type: "warning",
      content: "Posted entries cannot be edited\u2014this ensures audit integrity. To correct a posted entry, void it (which creates an automatic reversal) and create a new correct entry. The audit trail shows all changes."
    },
    {
      heading: "Entry Sources",
      content: "Entries can be created manually, from bank transactions, from invoices/bills, or from recurring templates. The source is tracked for reference."
    }
  ],
  tips: [
    "Add a clear memo to every entry explaining the business purpose",
    "Attach supporting documents (receipts, invoices) for easier audits",
    "Use recurring entries for predictable transactions like rent or subscriptions",
    "Review drafts regularly\u2014they don't affect reports until posted"
  ],
  warnings: [
    "Posted entries cannot be edited\u2014void and recreate if corrections needed",
    "Debits must equal credits before an entry can be posted"
  ]
};

// ============================================
// GENERAL LEDGER HELP CONTENT
// ============================================
export const generalLedgerHelp = {
  title: "General Ledger Help",
  description: "Viewing your complete transaction history",
  sections: [
    {
      heading: "What is the General Ledger?",
      content: "The General Ledger is your complete record of all posted transactions. It shows every debit and credit that has affected each account, with running balances."
    },
    {
      heading: "Running Balance",
      type: "info",
      content: "The running balance shows the cumulative effect of transactions on an account. It starts from the account's opening balance (if any) and updates with each transaction in chronological order."
    },
    {
      heading: "Filtering and Drill-Down",
      content: "Filter by account, date range, or amount. Click any entry to see the full journal entry and related details. Use this for account reconciliation and research."
    },
    {
      heading: "Only Posted Entries",
      type: "warning",
      content: "The General Ledger only shows posted entries. Draft entries do not appear here. This ensures the ledger reflects your official books."
    }
  ],
  tips: [
    "Use date filters to focus on specific periods during reconciliation",
    "Export filtered results for detailed analysis in spreadsheets",
    "Click transaction references to jump to the source entry",
    "Compare GL totals to sub-ledger totals to verify tie-outs"
  ],
  warnings: [
    "Running balances assume chronological viewing—changing filters may show different subtotals",
    "Only posted transactions appear in the General Ledger"
  ]
};

// ============================================
// AUDIT LOG HELP CONTENT
// ============================================
export const auditLogHelp = {
  title: "Audit Log Help",
  description: "Tracking all changes in your workspace",
  sections: [
    {
      heading: "What Gets Logged?",
      content: "Every significant action is recorded: user logins, entry creations, postings, voids, role changes, setting updates, and more. The log captures who did what, when, and the before/after values for changes."
    },
    {
      heading: "Using Filters",
      type: "info",
      content: "Filter by action type (create, update, delete, post), resource type (journal entry, invoice, contact), user, or date range. Use filters to investigate specific events or periods."
    },
    {
      heading: "Event Details",
      content: "Click any event to see full details including the exact changes made. For data changes, you'll see both the old and new values. IP addresses are logged for security."
    },
    {
      heading: "Retention",
      type: "warning",
      content: "Audit logs are retained indefinitely and cannot be modified or deleted. This ensures complete compliance and audit trail integrity."
    }
  ],
  tips: [
    "Review the audit log periodically to spot unusual activity",
    "Use filters to quickly find specific user actions",
    "Export audit data for compliance reports",
    "Check audit logs when reconciling discrepancies"
  ],
  warnings: [
    "Audit logs cannot be deleted or modified",
    "All workspace members can view the audit log (permissions may vary)"
  ]
};

// ============================================
// ADMIN PANEL HELP CONTENT
// ============================================
export const adminPanelHelp = {
  title: "Admin Panel Help",
  description: "Managing your workspace and team",
  sections: [
    {
      heading: "System Health",
      content: "The Overview tab shows real-time stats: active users, workspace count, bank connection status, and pending AI suggestions. Use this to monitor overall system health."
    },
    {
      heading: "Team Management",
      type: "info",
      content: (
        <div className="space-y-2">
          <p><strong>Owner:</strong> Full access, can delete workspace.</p>
          <p><strong>Admin:</strong> Full access except workspace deletion and owner management.</p>
          <p><strong>Accountant:</strong> GL, AR, AP, and reports access.</p>
          <p><strong>Bookkeeper:</strong> GL, AR, AP access (no reports).</p>
          <p><strong>Read Only:</strong> View reports only.</p>
        </div>
      )
    },
    {
      heading: "User Search & Impersonation",
      type: "warning",
      content: "Admins can search for users and impersonate them to troubleshoot issues. Impersonation is logged in the audit trail. Always exit impersonation mode when done."
    },
    {
      heading: "Feature Flags",
      content: "Control feature rollouts and experimental features. Some features can be gradually rolled out to a percentage of users. Use with caution—changes affect all users immediately."
    }
  ],
  tips: [
    "Review team roles periodically and remove inactive members",
    "Use impersonation sparingly and only for support purposes",
    "Check system health regularly for connection issues",
    "Test new features with feature flags before full rollout"
  ],
  warnings: [
    "Role changes take effect immediately",
    "Impersonation is logged—use responsibly"
  ]
};

// ============================================
// CONTACTS HELP CONTENT
// ============================================
export const contactsHelp = {
  tourId: "contacts",
  title: "Contacts Help",
  description: "Managing customers and vendors",
  sections: [
    {
      heading: "Contact Types",
      content: (
        <div className="space-y-2">
          <p><strong>Customer:</strong> People or businesses who buy from you. Linked to invoices and AR.</p>
          <p><strong>Vendor:</strong> People or businesses you buy from. Linked to bills and AP.</p>
          <p><strong>Both:</strong> Contacts who are both customers and vendors.</p>
        </div>
      )
    },
    {
      heading: "Contact Balance",
      type: "info",
      content: "Each contact shows their current balance: what customers owe you (positive AR) or what you owe vendors (positive AP). Balances update automatically as invoices and payments are recorded."
    },
    {
      heading: "Notes Timeline",
      content: "Add dated notes to any contact for CRM-style tracking. Notes are append-only and timestamped, creating a history of interactions."
    },
    {
      heading: "Address Fields",
      content: "Structured address fields (street, city, state, zip, country) enable formatted address printing on invoices and better organization."
    }
  ],
  tips: [
    "Keep contact information up to date for accurate invoicing",
    "Use notes to track important conversations or agreements",
    "Review customer balances regularly to follow up on overdue amounts",
    "Merge duplicate contacts rather than deleting (preserves history)"
  ],
  warnings: [
    "Deleting a contact with transaction history is not allowed",
    "Contact balances are calculated from posted transactions only"
  ]
};

// ============================================
// SETTINGS HELP CONTENT
// ============================================
export const settingsHelp = {
  tourId: "settings",
  title: "Settings Help",
  description: "Configuring your workspace",
  sections: [
    {
      heading: "Workspace Settings",
      content: "Set your company name, base currency, and fiscal year. These settings affect how data is displayed and calculated throughout the system."
    },
    {
      heading: "Integrations",
      type: "info",
      content: "Connect external services like Plaid for banking, payment processors, and more. Each integration has its own configuration requirements. Test connections before relying on them."
    },
    {
      heading: "Team Settings",
      content: "Manage workspace members, invite new users, and set default permissions. Changes to team settings are logged in the audit trail."
    },
    {
      heading: "API & Webhooks",
      type: "warning",
      content: "Advanced users can configure API access and webhooks for custom integrations. API keys should be treated like passwords—keep them secure."
    }
  ],
  tips: [
    "Test integrations in sandbox mode before connecting production accounts",
    "Review workspace settings after initial setup for accuracy",
    "Keep team permissions tight—grant minimum required access",
    "Regenerate API keys periodically for security"
  ],
  warnings: [
    "Changing base currency affects all future transactions",
    "Integration disconnection may affect automated workflows"
  ]
};

export default {
  bankingHelp,
  aiBuckHelp,
  billsHelp,
  invoicesHelp,
  dashboardHelp,
  reconciliationHelp,
  reconcileBuckHelp,
  pdfStatementHelp,
  moneyOutlookHelp,
  chartOfAccountsHelp,
  journalEntriesHelp,
  generalLedgerHelp,
  auditLogHelp,
  adminPanelHelp,
  contactsHelp,
  settingsHelp
};

// ============================================
// TASK MANAGER HELP CONTENT
// ============================================
export const taskManagerHelp = {
  tourId: "tasks",
  title: "Task Manager Help",
  description: "Organize and assign work across your team",
  sections: [
    {
      heading: "How It Works",
      content: (
        <div className="space-y-2">
          <p><strong>Tasks</strong> are work items that you or your team need to complete. Think of them like a to-do list for your accounting work.</p>
          <p><strong>Kanban Board:</strong> A visual board with columns. Each column is a status (To Do, In Progress, Review, Done). Drag cards between columns to update their status.</p>
          <p><strong>List View:</strong> A traditional table view — click column headers to sort. Better for scanning lots of tasks quickly.</p>
        </div>
      )
    },
    {
      heading: "Task Fields Explained",
      content: (
        <div className="space-y-2">
          <p><strong>Status:</strong> Where the task is in your workflow (To Do → In Progress → Review → Done)</p>
          <p><strong>Priority:</strong> How urgent it is (Low, Normal, High, Urgent)</p>
          <p><strong>Category:</strong> The type of work (Bookkeeping, Tax Filing, Reconciliation, etc.)</p>
          <p><strong>Assignee:</strong> Who is responsible — color-coded so you can spot assignments at a glance</p>
          <p><strong>Due Date:</strong> When it needs to be done. Overdue tasks are highlighted in red.</p>
        </div>
      )
    },
    {
      heading: "Customization",
      type: "info",
      content: "Click 'Customize' to add your own statuses, priorities, categories, and assign colors to team members. Your Kanban columns automatically update to match your custom statuses."
    },
    {
      heading: "Drag & Drop",
      type: "info",
      content: "On the Kanban board, click and hold any task card, then drag it to another column to instantly update its status. The column will highlight when you can drop."
    }
  ],
  tips: [
    "Use the Kanban board for a visual overview, switch to List for bulk management",
    "Color-code your team members in Customize so you can spot assignments instantly",
    "Set due dates to get overdue alerts — they show up in red",
    "Use Saved Views to quickly filter (e.g., 'My Tasks', 'Urgent')"
  ],
  warnings: [
    "Deleting a task also removes any time entries linked to it",
    "Custom statuses become Kanban columns — keep them manageable (4-6 is ideal)"
  ]
};

// ============================================
// TIME TRACKING HELP CONTENT
// ============================================
export const timeTrackingHelp = {
  tourId: "time_tracking",
  title: "Time Tracking Help",
  description: "Track hours for billing and productivity",
  sections: [
    {
      heading: "Two Ways to Track Time",
      content: (
        <div className="space-y-2">
          <p><strong>Live Timer:</strong> Click 'Start' to begin tracking in real-time. A running clock appears at the top. Click 'Stop' when done — it automatically creates a time entry.</p>
          <p><strong>Manual Entry:</strong> Click 'Manual Entry' to log time after the fact. Enter the date, hours, minutes, and description.</p>
        </div>
      )
    },
    {
      heading: "Billable vs. Non-Billable",
      content: (
        <div className="space-y-2">
          <p><strong>Billable:</strong> Time you can charge to a client. Set an hourly rate and the amount is calculated automatically. These entries can be converted into invoices.</p>
          <p><strong>Non-Billable:</strong> Internal work (admin, meetings, training) that isn't charged to clients. Still useful for tracking where your time goes.</p>
        </div>
      )
    },
    {
      heading: "Generate Invoice",
      type: "info",
      content: "Click 'Generate Invoice' to turn unbilled time entries into a client invoice. Select a client, pick which entries to include, customize the line items, and create a draft or posted invoice in one flow."
    },
    {
      heading: "Task Types",
      type: "info",
      content: "Click 'Task Types' to manage the categories for your time entries (e.g., Bookkeeping, Tax Prep, Consulting). You can add custom types specific to your practice."
    }
  ],
  tips: [
    "Always set an hourly rate on billable entries to auto-calculate amounts",
    "Link entries to a client to track per-client profitability",
    "Use 'Generate Invoice' to turn tracked time into client bills with one click",
    "The summary cards show your total billable amount — great for revenue tracking"
  ],
  warnings: [
    "Entries marked 'billed' won't appear in the Generate Invoice flow again",
    "Stopping a timer with less than 1 minute logged may not create an entry"
  ]
};

// ============================================
// RECURRING ENTRIES HELP CONTENT
// ============================================
export const recurringEntriesHelp = {
  tourId: "recurring",
  title: "Recurring Entries Help",
  description: "Automate repeating journal entries",
  sections: [
    {
      heading: "What Are Recurring Entries?",
      content: (
        <div className="space-y-2">
          <p>Recurring journal entries are <strong>templates</strong> for transactions that happen on a regular schedule — like monthly rent, loan payments, or depreciation.</p>
          <p>Instead of creating the same entry every month, set it up once and generate it with one click when it's due.</p>
        </div>
      )
    },
    {
      heading: "Auto-Post vs. Draft",
      content: (
        <div className="space-y-2">
          <p><strong>Auto-Post:</strong> When you click Generate, the journal entry is immediately posted to your ledger. Best for routine entries you trust.</p>
          <p><strong>Draft:</strong> The entry is created but not posted — you review and approve it first. Safer for entries where amounts might change.</p>
        </div>
      )
    },
    {
      heading: "Frequencies",
      type: "info",
      content: "Available schedules: Daily, Weekly, Bi-Weekly, Monthly, Quarterly, and Yearly. The 'Next Date' advances automatically after each generation."
    }
  ],
  tips: [
    "Use for rent, insurance, loan payments, depreciation, and payroll accruals",
    "Set an end date for temporary recurring entries (e.g., a 12-month lease)",
    "Check the 'Due' alert banner — it tells you when entries need to be generated",
    "Pause recurring entries during off-seasons instead of deleting them"
  ],
  warnings: [
    "Auto-posted entries immediately affect your financial statements",
    "Debits must equal credits — the system validates this before saving"
  ]
};

// ============================================
// DOCUMENT INBOX HELP CONTENT
// ============================================
export const documentInboxHelp = {
  tourId: "document_inbox",
  title: "Document Inbox Help",
  description: "Upload and process financial documents with AI",
  sections: [
    {
      heading: "How It Works",
      content: (
        <div className="space-y-2">
          <p><strong>1. Upload:</strong> Drop PDFs, images, or spreadsheets into the inbox (receipts, vendor invoices, bank statements, etc.)</p>
          <p><strong>2. Process:</strong> Click 'Process with AI' on any pending document. AI reads the document and extracts vendor name, date, amounts, and line items.</p>
          <p><strong>3. Review:</strong> Check the extracted data for accuracy. Approve, file, or reject.</p>
        </div>
      )
    },
    {
      heading: "Document Statuses",
      content: (
        <div className="space-y-2">
          <p><strong>Pending:</strong> Uploaded but not yet processed by AI</p>
          <p><strong>Processing:</strong> AI is currently reading the document</p>
          <p><strong>Ready for Review:</strong> AI extraction complete — check the results</p>
          <p><strong>Approved:</strong> You've confirmed the data is correct</p>
          <p><strong>Filed:</strong> Archived for your records</p>
        </div>
      )
    },
    {
      heading: "AI Processing",
      type: "warning",
      content: "Processing a document uses AI credits. Upload first, then only process the documents you need extracted. You won't be charged for uploading — only for clicking 'Process with AI'."
    }
  ],
  tips: [
    "Upload receipts as soon as you get them — don't let them pile up",
    "AI works best with clear, high-resolution images and PDFs",
    "Use the search bar to find documents by vendor name or description",
    "Filed documents are your permanent searchable archive"
  ],
  warnings: [
    "AI extraction isn't perfect — always review the results before approving",
    "Processing uses credits — only process documents you need extracted",
    "Maximum file size is 15MB per document"
  ]
};

// ============================================
// CFO BUCK HELP CONTENT
// ============================================
export const cfoBuckHelp = {
  tourId: "cfo_buck",
  title: "CFO Buck Help",
  description: "Your AI financial advisor",
  sections: [
    {
      heading: "What Is CFO Buck?",
      content: (
        <div className="space-y-2">
          <p>CFO Buck is your <strong>AI-powered financial advisor</strong>. Ask questions about your finances in plain English and get insights based on your actual data.</p>
          <p>Think of it as having a CFO you can chat with anytime — it understands your chart of accounts, transactions, and financial position.</p>
        </div>
      )
    },
    {
      heading: "What To Ask",
      type: "info",
      content: (
        <div className="space-y-1">
          <p>"What were my biggest expenses last month?"</p>
          <p>"Am I on track to be profitable this quarter?"</p>
          <p>"How does my revenue compare to last year?"</p>
          <p>"What's my current cash position?"</p>
          <p>"Should I be concerned about any financial trends?"</p>
        </div>
      )
    }
  ],
  tips: [
    "The more data in your system (transactions, invoices), the better the insights",
    "Ask follow-up questions — CFO Buck remembers the conversation context",
    "Use it for quick answers instead of running complex reports manually"
  ],
  warnings: [
    "AI-generated insights are advisory — always verify important decisions",
    "Each message uses AI credits"
  ]
};

// ============================================
// REPORTS HELP CONTENT
// ============================================
export const reportsHelp = {
  tourId: "reports",
  title: "Reports Help",
  description: "Financial reporting and analysis",
  sections: [
    {
      heading: "Available Reports",
      content: (
        <div className="space-y-2">
          <p><strong>Trial Balance:</strong> A list of all accounts and their balances. If debits equal credits, your books are balanced.</p>
          <p><strong>Income Statement (P&L):</strong> Shows revenue minus expenses over a period. Tells you if you're profitable.</p>
          <p><strong>Balance Sheet:</strong> A snapshot of what you own (assets), owe (liabilities), and your net worth (equity) at a point in time.</p>
          <p><strong>Cash Flow:</strong> Tracks cash in and out — critical for understanding liquidity.</p>
          <p><strong>AR/AP Aging:</strong> Shows who owes you money and who you owe, broken down by how overdue.</p>
        </div>
      )
    },
    {
      heading: "Multi-Entity Consolidation",
      type: "info",
      content: "Select multiple workspaces to see consolidated reports across entities. Great for business owners with multiple companies."
    },
    {
      heading: "Export Options",
      type: "info",
      content: "Every report can be exported to CSV (for spreadsheets), Excel (.xlsx), or PDF. Click the export dropdown on any report."
    }
  ],
  tips: [
    "Run Trial Balance monthly to catch errors early",
    "Income Statement is your go-to for 'are we making money?'",
    "Use Balance Sheet for loan applications and investor meetings",
    "AR Aging helps you follow up on late-paying customers"
  ],
  warnings: [
    "Reports only include posted transactions — drafts are excluded",
    "Date ranges matter — make sure you're looking at the right period"
  ]
};

export const allHelp = {
  bankingHelp,
  aiBuckHelp,
  billsHelp,
  invoicesHelp,
  dashboardHelp,
  reconciliationHelp,
  reconcileBuckHelp,
  pdfStatementHelp,
  moneyOutlookHelp,
  chartOfAccountsHelp,
  journalEntriesHelp,
  generalLedgerHelp,
  auditLogHelp,
  adminPanelHelp,
  contactsHelp,
  settingsHelp,
  taskManagerHelp,
  timeTrackingHelp,
  recurringEntriesHelp,
  documentInboxHelp,
  cfoBuckHelp,
  reportsHelp,
};
